import { User } from '@/lib/AuthenticatorPlugin.ts'
import { useFreemium } from '@/composables/freemium/useFreemium.ts'
import getUserState from '@/composables/users/getUserState.ts'
import { isCFSWhitelabel } from '@/composables/isCFSWhitelabel.ts'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'

type AccountStatus = {
  has_account: boolean
  has_verified_mobile: boolean
  is_super_fund_member: boolean
  has_member_id: boolean
  has_super_account: boolean
  has_super_account_number: boolean
  has_fee_consent: boolean
  dtd_member: boolean
}

type RouteResult = { name: string | undefined }

/**
 * Determines the appropriate route for a user based on their account status and onboarding progress
 */
export default async (auth0User: User): Promise<RouteResult> => {
  const accountStatus = (await getUserState()) as AccountStatus | undefined
  const { freemiumEnabled } = useFreemium()
  const dtdEnabled = useLDFlag(FeatureList.ENABLE_DTD, false)
  const isCFS = isCFSWhitelabel()
  const dtdReady: boolean = dtdEnabled.value && !!isCFS

  // Helper to get initial form route based on whitelabel
  const getInitialFormRoute = (): string => {
    return dtdReady ? 'CFSDisclaimer' : 'UserDetailsForm'
  }

  // First time user - no account status exists
  if (!accountStatus || accountStatus.has_account === false) {
    return {
      name: freemiumEnabled.value ? 'get-started' : getInitialFormRoute(),
    }
  }

  // Legacy user who hasn't completed onboarding
  if (auth0User.legacy_user && !accountStatus.dtd_member) {
    return {
      name: freemiumEnabled.value ? 'get-started' : getInitialFormRoute(),
    }
  }

  // Superfund-created users who haven't completed DTD setup
  if (!accountStatus.dtd_member && auth0User.user_role === 'member') {
    // For CFS users if they haven't completed onboarding, redirect back to disclaimer to restart flow
    if (dtdReady && !accountStatus.has_super_account) {
      return { name: 'CFSDisclaimer' }
    }
    return { name: undefined }
  }

  // Determine next step in account creation flow
  return getAccountCreationStep(accountStatus, freemiumEnabled.value, dtdReady)
}

/**
 * Determines the next step in the account creation process based on current status
 */
const getAccountCreationStep = (
  accountStatus: AccountStatus,
  isFreemiumEnabled: boolean,
  dtdReady: boolean,
): RouteResult => {
  // User hasn't created basic account yet
  if (!accountStatus.has_account) {
    return {
      name: isFreemiumEnabled ? 'get-started' : dtdReady ? 'CFSDisclaimer' : 'UserDetailsForm',
    }
  }

  // Super fund member flow
  if (accountStatus.is_super_fund_member) {
    if (!accountStatus.has_member_id) return { name: 'CaptureFundMemberId' }
    if (!accountStatus.has_super_account || !accountStatus.has_super_account_number) {
      return { name: 'CaptureSuperAccountNumber' }
    }
    if (!accountStatus.has_fee_consent) return { name: 'SuperFundDisclaimer' }
  } else {
    // Regular user flow
    if (!accountStatus.has_super_account) return { name: 'SelectSuperFund' }
    if (!accountStatus.has_fee_consent) return { name: 'TermsAndConditionsForm' }
  }

  // Account creation complete
  return { name: undefined }
}
